import { isShipped, SHIPPED_LOCALES, type Locale, type ShippedLocale } from './locale.ts';

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

const KEY = 'tutor.locale';

function browserStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * `vi-VN` and `VI` are `vi`; a tag no catalog matches is `null`. Matched on
 * the base subtag alone while every shipped catalog is named by a language
 * only. A catalog named by script or region (a5c9cb4's `zh-Hant`, `pt-BR`)
 * needs `Intl.Locale.maximize` and a rule of its own here.
 */
function shippedFor(tag: string | null | undefined): ShippedLocale | null {
  if (!tag) return null;
  const exact = SHIPPED_LOCALES.find((l) => l.toLowerCase() === tag.toLowerCase());
  if (exact) return exact;
  try {
    const { language } = new Intl.Locale(tag);
    return isShipped(language) ? language : null;
  } catch {
    return null;
  }
}

/**
 * The locale a page opens in, before any learner is known (spec §3.2): `?lang`
 * (a mail link carries it), the last locale this browser showed, the
 * browser's own languages, English. A signed-in learner's own locale replaces
 * this as soon as `me` answers.
 *
 * Read once at startup, so `?lang=qps` survives navigation, which drops the
 * query string.
 */
export function initialLocale(search: string, remembered: string | null, languages: readonly string[]): Locale {
  const asked = new URLSearchParams(search).get('lang');
  if (asked === 'qps') return 'qps';
  for (const tag of [asked, remembered, ...languages]) {
    const locale = shippedFor(tag);
    if (locale) return locale;
  }
  return 'en';
}

/**
 * A per-browser convenience, so a reload or a signed-out page opens in the
 * language last shown. Harmless when storage is blocked: the learner's own
 * locale arrives with `me` either way.
 */
export function rememberLocale(locale: ShippedLocale, storage: Storage | null = browserStorage()): void {
  try {
    storage?.setItem(KEY, locale);
  } catch {
    // A private window or blocked site data: nothing to remember into.
  }
}

export function rememberedLocale(storage: Storage | null = browserStorage()): string | null {
  try {
    return storage?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
}
